import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Lang = 'en' | 'ar';
export type Dir = 'ltr' | 'rtl';

/**
 * Lightweight, dependency-free i18n. Strings live in the dictionary below;
 * `t(key)` falls back to the key when a translation is missing, so untranslated
 * UI still renders. Arabic switches the document to RTL.
 */
const DICT: Record<Lang, Record<string, string>> = {
  en: {
    'nav.projects': 'Projects',
    'nav.agents': 'Agents',
    'nav.members': 'Members',
    'nav.orgSettings': 'Organization settings',
    'nav.aiSettings': 'AI settings',
    'nav.security': 'Security',
    'nav.signOut': 'Sign out',
    'common.language': 'Language',
    'a11y.notifications': 'Notifications',
    'a11y.assistant': 'Open AI assistant',
    'a11y.account': 'Account menu',
  },
  ar: {
    'nav.projects': 'المشاريع',
    'nav.agents': 'الوكلاء',
    'nav.members': 'الأعضاء',
    'nav.orgSettings': 'إعدادات المؤسسة',
    'nav.aiSettings': 'إعدادات الذكاء الاصطناعي',
    'nav.security': 'الأمان',
    'nav.signOut': 'تسجيل الخروج',
    'common.language': 'اللغة',
    'a11y.notifications': 'الإشعارات',
    'a11y.assistant': 'فتح المساعد الذكي',
    'a11y.account': 'قائمة الحساب',
  },
};

const RTL_LANGS = new Set<Lang>(['ar']);
const STORAGE_KEY = 'content-agents:lang';

interface I18nValue {
  lang: Lang;
  dir: Dir;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

const initialLang = (): Lang => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return stored === 'ar' || stored === 'en' ? stored : 'en';
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const dir: Dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const t = useCallback((key: string) => DICT[lang][key] ?? DICT.en[key] ?? key, [lang]);

  const value = useMemo<I18nValue>(() => ({ lang, dir, setLang, t }), [lang, dir, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}
